import { useState } from 'react';
import { getLessonMaterialUrl } from '@/lib/api';
import { formatFileSize, materialKind } from '@/lib/lessonMedia';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  FileText,
  FileSpreadsheet,
  Presentation,
  Image as ImageIcon,
  Music,
  Archive,
  File,
  Download,
  Loader2,
  Trash2,
  Paperclip,
} from 'lucide-react';

const KIND_STYLES = {
  pdf: { Icon: FileText, tone: 'text-rose-600 bg-rose-50' },
  doc: { Icon: FileText, tone: 'text-blue-600 bg-blue-50' },
  sheet: { Icon: FileSpreadsheet, tone: 'text-emerald-600 bg-emerald-50' },
  slides: { Icon: Presentation, tone: 'text-amber-600 bg-amber-50' },
  image: { Icon: ImageIcon, tone: 'text-purple-600 bg-purple-50' },
  audio: { Icon: Music, tone: 'text-indigo-600 bg-indigo-50' },
  archive: { Icon: Archive, tone: 'text-slate-600 bg-slate-100' },
  file: { Icon: File, tone: 'text-[#062B1A]/60 bg-[#F7F8FA]' },
};

/**
 * The list of downloadable files attached to a lesson.
 *
 * Files live in a private bucket, so a download is a two-step exchange: ask the
 * API for a short-lived presigned URL, then open it. `onRemove` is only passed
 * in the instructor editor, which is what turns this into an editable list.
 */
export default function LessonMaterials({ lessonId, materials, onRemove, emptyLabel }) {
  const [busyId, setBusyId] = useState('');
  const items = materials || [];

  const handleDownload = async (material) => {
    try {
      setBusyId(material.id);
      const res = await getLessonMaterialUrl(lessonId, material.id);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Download link could not be created.');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Download link could not be created.');
    } finally {
      setBusyId('');
    }
  };

  if (!items.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Paperclip className="w-8 h-8 text-[#062B1A]/15" />
        <p className="text-sm text-[#062B1A]/50">
          {emptyLabel || 'No materials have been added to this lesson yet.'}
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-2" data-testid="lesson-materials">
      {items.map((material) => {
        const { Icon, tone } = KIND_STYLES[materialKind(material.content_type)] || KIND_STYLES.file;
        const size = formatFileSize(material.size);
        return (
          <li
            key={material.id}
            className="flex items-center gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-3 transition-colors hover:border-[#D4AF37]/40"
          >
            <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${tone}`}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[#062B1A]">
                {material.title || material.filename}
              </p>
              <p className="truncate text-xs text-[#062B1A]/45">
                {material.filename}
                {size ? ` • ${size}` : ''}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="flex-shrink-0 border-[rgba(6,43,26,0.15)]"
              disabled={busyId === material.id}
              onClick={() => handleDownload(material)}
              data-testid={`download-material-${material.id}`}
            >
              {busyId === material.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              <span className="ml-1.5 hidden sm:inline">Download</span>
            </Button>
            {onRemove && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="flex-shrink-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => onRemove(material)}
                aria-label={`Remove ${material.title || material.filename}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
